import React, { useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { createEntity, updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { savePolicy } from '../../data/policy';
import PolicyHistory from './PolicyHistory';
import { apiPost } from '../../utils/backendApi';
import { useAuth } from '../../AuthContext';

// Human Review — the queues every gate feeds: workflow control.review
// blocks, knowledge guardrails (uncertainToReview), NoVA handoffs, QA
// gates. Members triage (queue / priority / assignee / due), decide
// (Approve · Return · Escalate), and close the loop: a decision can become
// a knowledge doc or a test case. Routing rules live on the org doc and
// are applied server-side when items are created.

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const REASONS = ['policy', 'missing-evidence', 'low-confidence', 'returned', 'handoff', 'gate'];
const PRIORITY_TONE = { urgent: 'danger', high: 'warn', normal: 'info', low: 'muted' };

export default function ReviewModule() {
  const { org, myRole } = useOutletContext();
  const { user } = useAuth();
  const isAdmin = ['admin', 'owner'].includes(myRole);
  const [tab, setTab] = useModuleTab('queue');
  const [params] = useSearchParams();
  const focusId = params.get('item');
  // ?app=<builder app id> (Operate's "review queue" link): show only items the
  // app's own agents raised — by source.appId or source.agentId.
  const appFilterId = params.get('app');
  const { docs: builderApps } = useEntityList('app', { org: org.id });
  const appFilter = appFilterId ? (builderApps || []).find((a) => a.id === appFilterId) || null : null;
  const forApp = (i) => !appFilterId || i.source?.appId === appFilterId || (appFilter?.links?.agentIds || []).includes(i.source?.agentId);
  const { docs: items } = useEntityList('reviewItem', { org: org.id });
  const { docs: members } = useEntityList('member', { org: org.id });
  const [queueFilter, setQueueFilter] = useState('all');
  const [mineOnly, setMineOnly] = useState(false);
  const [filing, setFiling] = useState(false);

  // Mirrors the reviewItem Firestore rule + backend canDecideReview(): decide
  // needs the reviewer capability or admin+; triage is open to every member.
  const me = (members || []).find((m) => m.uid === user?.uid);
  const canDecide = isAdmin || (Array.isArray(me?.capabilities) && me.capabilities.includes('reviewer'));

  const all = items || [];
  const pending = all.filter((i) => i.status === 'pending' && forApp(i));
  const queues = Array.from(new Set(pending.map((i) => i.queue || 'general'))).sort();
  const now = Date.now();
  const overdue = pending.filter((i) => i.dueAt && i.dueAt < now);
  const shown = pending
    .filter((i) => queueFilter === 'all' || (i.queue || 'general') === queueFilter)
    .filter((i) => !mineOnly || i.assigneeUid === user.uid)
    .sort((a, b) => (a.id === focusId ? -1 : b.id === focusId ? 1 : 0) || PRIORITIES.indexOf(b.priority || 'normal') - PRIORITIES.indexOf(a.priority || 'normal') || (a.dueAt || Infinity) - (b.dueAt || Infinity) || (a.createdAt || 0) - (b.createdAt || 0));
  const decided = all.filter((i) => i.status !== 'pending').sort((a, b) => (b.decidedAt || 0) - (a.decidedAt || 0)).slice(0, 50);
  const memberName = (uid) => (members || []).find((m) => m.uid === uid)?.displayName || (members || []).find((m) => m.uid === uid)?.email || uid || '—';

  return (
    <ModuleFrame
      title="Human Review"
      subtitle="Queues · Routing · Decisions · Feedback loop"
      accent="#e8b34b"
      note={`Human Review, tab ${tab}: ${pending.length} pending${overdue.length ? `, ${overdue.length} overdue` : ''}${queues.length ? `, queues ${queues.join(', ')}` : ''}${appFilterId ? ', filtered to one app' : ''}. NoVA: list_reviewItem, file_review_item; Approve / Reject are the person's clicks; routing rules via set_org_policy (reviewRouting).`}
      help="Everything a gate paused waits here. Decide with context, and turn the decision into something durable — a knowledge doc, a test case, a policy."
      context={(
        <>
          {appFilterId && <Chip tone="info">{appFilter ? appFilter.name : 'this app'} only · <Link to={`/orgs/${org.id}/human-review`} style={{ color: 'inherit' }}>all</Link></Chip>}
          <Chip tone={pending.length ? 'warn' : 'ok'}>{pending.length} pending</Chip>
          {overdue.length > 0 && <Chip tone="danger">{overdue.length} overdue</Chip>}
          {pending.some((i) => i.assigneeUid) && (
            <label className="mf-toggle" style={{ padding: '4px 8px' }} title="Routing rules assign items to people; this shows only yours."><input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} /> assigned to me ({pending.filter((i) => i.assigneeUid === user.uid).length})</label>
          )}
        </>
      )}
      tabs={[
        { id: 'queue', label: 'Queue', count: pending.length },
        { id: 'history', label: 'Decision history', count: decided.length },
        { id: 'routing', label: 'Routing rules', count: (org.reviewRouting?.rules || []).length },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: 'Work the queue', hint: 'oldest urgent first', onClick: () => setTab('queue'), primary: true },
        { label: 'Routing rules', hint: isAdmin ? 'who gets what' : 'admin', onClick: () => setTab('routing') },
        { label: 'File a review', hint: 'manual handoff', onClick: () => { setTab('queue'); setFiling(true); } },
      ]}
    >
      {tab === 'queue' && filing && <FileReviewForm orgId={org.id} queues={queues} onDone={() => setFiling(false)} />}
      {tab === 'queue' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Queues" meta="filter">
            <ul className="mf-list">
              <li><button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)', borderColor: queueFilter === 'all' ? 'rgba(232,179,75,0.5)' : undefined }} onClick={() => setQueueFilter('all')}><span className="mf-row-grow" style={{ textAlign: 'left' }}>All</span><span className="mf-muted">{pending.length}</span></button></li>
              {queues.map((q) => (
                <li key={q}><button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)', borderColor: queueFilter === q ? 'rgba(232,179,75,0.5)' : undefined }} onClick={() => setQueueFilter(q)}><span className="mf-row-grow" style={{ textAlign: 'left' }}>{q}</span><span className="mf-muted">{pending.filter((i) => (i.queue || 'general') === q).length}</span></button></li>
              ))}
            </ul>
            <p className="mf-muted" style={{ marginTop: 10 }}>Queues are named by whoever files the item (workflow block, guardrail, handoff). Routing rules can reassign.</p>
          </ModuleFrame.Panel>
          <div className="mf-span-2" style={{ display: 'grid', gap: 10 }}>
            {!shown.length && <div className="cs-empty">Nothing waiting{mineOnly ? ' on you' : ''}.</div>}
            {shown.map((item) => (
              <PendingCard key={item.id} item={item} orgId={org.id} members={members || []} memberName={memberName} focused={item.id === focusId} canDecide={canDecide} />
            ))}
          </div>
        </ModuleFrame.Columns>
      )}

      {tab === 'history' && (
        <ModuleFrame.Panel title="Decisions" meta="latest 50">
          <table className="mf-table">
            <thead><tr><th>Decision</th><th>Queue</th><th>Reason</th><th>Content</th><th>Note</th><th>By</th><th>When</th><th>Became</th></tr></thead>
            <tbody>
              {decided.map((i) => (
                <tr key={i.id}>
                  <td><Chip tone={i.status === 'approved' ? 'ok' : i.decision === 'escalated' ? 'warn' : 'danger'}>{i.decision || i.status}</Chip></td>
                  <td>{i.queue || 'general'}</td>
                  <td className="mf-muted">{i.reason || '—'}</td>
                  <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.content}>{(i.content || '').slice(0, 140)}</td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.note || ''}>{i.note || <span className="mf-muted">—</span>}</td>
                  <td className="mf-muted">{memberName(i.decidedBy)}</td>
                  <td className="mf-muted">{i.decidedAt ? new Date(i.decidedAt).toLocaleString() : '—'}</td>
                  <td>{i.feedback?.kind ? <Link to={i.feedback.kind === 'knowledgeDoc' ? `/orgs/${org.id}/knowledge` : i.feedback.kind === 'testCase' ? `/orgs/${org.id}/qa` : `/orgs/${org.id}/knowledge?tab=guardrails`}>{i.feedback.kind}</Link> : <FeedbackButtons orgId={org.id} item={i} uid={user.uid} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModuleFrame.Panel>
      )}

      {tab === 'routing' && <RoutingRules org={org} isAdmin={isAdmin} members={members || []} />}
    </ModuleFrame>
  );
}

// Manual handoff — an in-page form (queue, priority, text), one record per
// submit; the backend routes it through the org's rules.
function FileReviewForm({ orgId, queues, onDone }) {
  const [content, setContent] = useState('');
  const [queue, setQueue] = useState(queues[0] || 'general');
  const [priority, setPriority] = useState('normal');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      await apiPost(`/review/${orgId}/handoff`, { content: content.trim(), queue: queue.trim() || 'general', priority, reason: 'handoff' });
      onDone();
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  return (
    <ModuleFrame.Panel title="File a review" meta="a person decides; routing rules may reassign">
      <form onSubmit={submit} style={{ display: 'grid', gap: 8 }}>
        <textarea className="mf-textarea" style={{ minHeight: 90 }} placeholder="What needs a human decision? Include the context the reviewer needs." value={content} onChange={(e) => setContent(e.target.value)} aria-label="What needs a human decision" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="cs-input" style={{ width: 180 }} list="rv-queues" value={queue} onChange={(e) => setQueue(e.target.value)} placeholder="queue" aria-label="Queue" />
          <datalist id="rv-queues">{queues.map((q) => <option key={q} value={q} />)}</datalist>
          <select className="cs-input" style={{ width: 130 }} value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Priority">
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <span style={{ flex: 1 }} />
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onDone} disabled={busy}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !content.trim()}>{busy ? 'Filing…' : 'File'}</button>
        </div>
        {err && <p className="cs-form-error" role="alert">{err}</p>}
      </form>
    </ModuleFrame.Panel>
  );
}

function PendingCard({ item, orgId, members, memberName, focused, canDecide }) {
  const { user } = useAuth();
  const [text, setText] = useState(item.content || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const patch = async (p) => {
    setBusy(true); setErr('');
    try { await updateEntity('reviewItem', { org: orgId }, item.id, p); } catch (e) { setErr(friendlyError(e)); } finally { setBusy(false); }
  };
  const decide = (status, decision) => patch({
    status, decision,
    ...(status === 'approved' && text !== item.content ? { editedContent: text } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
    decidedBy: user.uid, decidedAt: Date.now(),
  });
  const escalate = () => patch({ priority: 'urgent', escalatedAt: Date.now(), ...(note.trim() ? { note: note.trim() } : {}) });
  const overdue = item.dueAt && item.dueAt < Date.now();
  const src = item.source || {};
  return (
    <div className="cs-card" style={{ padding: 14, borderColor: focused ? 'rgba(232,179,75,0.6)' : undefined }} id={`review-${item.id}`}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip tone={PRIORITY_TONE[item.priority || 'normal']}>{item.priority || 'normal'}</Chip>
        <Chip tone="muted">{item.queue || 'general'}</Chip>
        <Chip tone="info">{item.reason || 'gate'}</Chip>
        {item.escalatedAt && <Chip tone="danger">escalated</Chip>}
        <span className="mf-muted">{new Date(item.createdAt).toLocaleString()}</span>
        {item.dueAt && <span className={overdue ? 'cs-form-error' : 'mf-muted'} style={{ margin: 0 }}>due {new Date(item.dueAt).toLocaleString()}</span>}
        <span style={{ flex: 1 }} />
        <select className="cs-input" value={item.priority || 'normal'} onChange={(e) => patch({ priority: e.target.value })} disabled={busy} aria-label="Priority">{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select>
        <select className="cs-input" value={item.assigneeUid || ''} onChange={(e) => patch({ assigneeUid: e.target.value || null })} disabled={busy} aria-label="Assignee">
          <option value="">unassigned</option>
          {members.map((m) => <option key={m.uid} value={m.uid}>{m.displayName || m.email}</option>)}
        </select>
      </div>
      <KV rows={[
        ['Source', src.runId ? `workflow run ${src.runId}` : src.mode === 'ask' ? `NoVA answer${src.appId ? ` · app ${src.appId}` : ''}` : src.mode === 'handoff' ? 'human handoff' : src.mode || '—'],
        ...(item.assigneeUid ? [['Assigned to', memberName(item.assigneeUid)]] : []),
      ]} />
      {item.instructions && <p className="cs-help" style={{ margin: '8px 0 0' }}>{item.instructions}</p>}
      <textarea className="mf-textarea" style={{ marginTop: 10, fontFamily: 'ui-monospace, Menlo, monospace', minHeight: 120 }} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} aria-label="Content under review" />
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }} data-agent-guard>
        <input className="cs-input" style={{ flex: 1, minWidth: 200 }} placeholder="Decision note (optional)…" value={note} onChange={(e) => setNote(e.target.value)} />
        {canDecide && (
          <>
            <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy} onClick={() => decide('approved', 'approved')}>Approve{text !== item.content ? ' with edits' : ''}</button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => decide('rejected', 'returned')} title="Reject with guidance — the author fixes and resubmits">Return</button>
          </>
        )}
        <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy || item.escalatedAt} onClick={escalate} title="Mark urgent and flag for a senior reviewer">Escalate</button>
        {canDecide && (
          <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={() => decide('rejected', 'rejected')}>Reject</button>
        )}
      </div>
      {!canDecide && (
        <p className="cs-help" style={{ margin: '8px 0 0', fontSize: 12 }}>
          Deciding needs the <strong>reviewer</strong> capability (Administration → Members) or an admin role. You can triage and escalate.
        </p>
      )}
      {err && <p className="cs-form-error" role="alert">{err}</p>}
    </div>
  );
}

// Feedback loop: turn a decided item into a knowledge draft or a QA case.
function FeedbackButtons({ orgId, item, uid }) {
  const { docs: suites } = useEntityList('testSuite', { org: orgId });
  const [err, setErr] = useState('');
  const asKnowledge = async () => {
    try {
      const body = item.editedContent || item.content || '';
      const id = await createEntity('knowledgeDoc', { org: orgId }, {
        title: `From review ${item.id.slice(0, 6)} — ${body.split('\n')[0].slice(0, 80)}`, content: body, sourceType: 'paste', tags: ['from-review'], createdBy: uid,
      });
      await updateEntity('reviewItem', { org: orgId }, item.id, { feedback: { kind: 'knowledgeDoc', id } });
    } catch (e) { setErr(friendlyError(e)); }
  };
  const asTest = async () => {
    const suite = (suites || [])[0];
    if (!suite) { setErr('Create a QA suite first.'); return; }
    try {
      const q = (item.content || '').match(/^Q:\s*(.+)$/m)?.[1] || (item.content || '').slice(0, 200);
      const id = await createEntity('testCase', { org: orgId, testSuite: suite.id }, {
        name: `Review ${item.id.slice(0, 6)}: ${q.slice(0, 60)}`, kind: 'knowledge-answer', input: { question: q }, expect: { citationsRequired: true },
      });
      await updateEntity('reviewItem', { org: orgId }, item.id, { feedback: { kind: 'testCase', id, suiteId: suite.id } });
    } catch (e) { setErr(friendlyError(e)); }
  };
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={asKnowledge} title="Draft a knowledge doc from this decision">→ knowledge</button>
      <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={asTest} title="Add a QA case that checks this">→ test</button>
      {err && <span className="cs-form-error" style={{ margin: 0 }}>{err}</span>}
    </span>
  );
}

function RoutingRules({ org, isAdmin, members }) {
  const rules = org.reviewRouting?.rules || [];
  const [err, setErr] = useState('');
  const save = async (next, escalateAfterMs = org.reviewRouting?.escalateAfterMs || null) => {
    setErr('');
    // Through the policy API: a numbered, restorable revision + audit row.
    try { await savePolicy(org.id, 'reviewRouting', { rules: next, escalateAfterMs }); } catch (e) { setErr(friendlyError(e)); }
  };
  const upd = (i, path, v) => save(rules.map((r, j) => (j === i ? { ...r, [path[0]]: { ...(r[path[0]] || {}), [path[1]]: v || undefined } } : r)));
  return (
    <ModuleFrame.Columns cols={2}>
      <ModuleFrame.Panel title="Rules" meta="first match wins — applied when an item is filed">
        {!rules.length && <p className="cs-help">No rules: items keep the queue and priority they were filed with.</p>}
        <table className="mf-table">
          <thead><tr><th>Name</th><th>If queue</th><th>If reason</th><th>→ queue</th><th>→ priority</th><th>→ assignee</th><th>→ due in</th><th /></tr></thead>
          <tbody>
            {rules.map((r, i) => (
              <tr key={i}>
                <td><input className="cs-input" value={r.name || ''} onChange={(e) => save(rules.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} disabled={!isAdmin} /></td>
                <td><input className="cs-input" value={r.match?.queue || ''} placeholder="any" onChange={(e) => upd(i, ['match', 'queue'], e.target.value)} disabled={!isAdmin} /></td>
                <td><select className="cs-input" value={r.match?.reason || ''} onChange={(e) => upd(i, ['match', 'reason'], e.target.value)} disabled={!isAdmin}><option value="">any</option>{REASONS.map((x) => <option key={x}>{x}</option>)}</select></td>
                <td><input className="cs-input" value={r.set?.queue || ''} placeholder="keep" onChange={(e) => upd(i, ['set', 'queue'], e.target.value)} disabled={!isAdmin} /></td>
                <td><select className="cs-input" value={r.set?.priority || ''} onChange={(e) => upd(i, ['set', 'priority'], e.target.value)} disabled={!isAdmin}><option value="">keep</option>{PRIORITIES.map((x) => <option key={x}>{x}</option>)}</select></td>
                <td><select className="cs-input" value={r.set?.assigneeUid || ''} onChange={(e) => upd(i, ['set', 'assigneeUid'], e.target.value)} disabled={!isAdmin}><option value="">nobody</option>{members.map((m) => <option key={m.uid} value={m.uid}>{m.displayName || m.email}</option>)}</select></td>
                <td><select className="cs-input" value={String(r.set?.dueInMs || '')} onChange={(e) => upd(i, ['set', 'dueInMs'], e.target.value ? Number(e.target.value) : '')} disabled={!isAdmin}><option value="">—</option><option value={3600000}>1 h</option><option value={14400000}>4 h</option><option value={86400000}>1 day</option><option value={259200000}>3 days</option></select></td>
                <td><button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!isAdmin} onClick={() => save(rules.filter((_, j) => j !== i))}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {isAdmin && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" style={{ marginTop: 8 }} onClick={() => save([...rules, { name: `rule ${rules.length + 1}`, match: {}, set: {} }])}>+ Rule</button>}
        {err && <p className="cs-form-error" role="alert">{err}</p>}
        <PolicyHistory org={org} isAdmin={isAdmin} area="reviewRouting" compact />
      </ModuleFrame.Panel>
      <ModuleFrame.Panel title="Escalation" meta="default due time when no rule sets one">
        <select className="cs-input" value={String(org.reviewRouting?.escalateAfterMs || '')} onChange={(e) => save(rules, e.target.value ? Number(e.target.value) : null)} disabled={!isAdmin} aria-label="Escalate after">
          <option value="">none</option><option value={3600000}>1 hour</option><option value={14400000}>4 hours</option><option value={86400000}>1 day</option>
        </select>
        <p className="mf-muted" style={{ marginTop: 10 }}>Items past their due time show as overdue in the queue header and the bell. Test routing with a <Link to={`/orgs/${org.id}/qa`}>review-routing QA case</Link>.</p>
      </ModuleFrame.Panel>
    </ModuleFrame.Columns>
  );
}
